import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Crown, Search, Check, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { metalFill } from "@/components/sales/SalesQuickFilter";

// Same section ↔ package-requirement mapping as the website catering page
export const SECTIONS = [
  ["starters", "starters", "Starters (2 pc each)"], ["specialBhaji", "special", "Special Bhaji (80 gms)"],
  ["simpleBhaji", "mains", "Simple Bhaji / Mains (80 gms)"], ["desserts", "dessert", "Sweets / Desserts (80 gms)"],
  ["roti", "roti", "Roti / Bhakari"], ["rice", "rice", "Rice (150 gms)"], ["drinks", "drink", "Drinks (200 ml)"],
  ["sides", "side", "Sides"], ["chutney", "chutney", "Chutney"],
];

export const pkgPrice = (p, intl) => Number(intl ? p?.price_per_person_aud : p?.price_per_person_inr) || 0;

export function selectionGaps(pkg, sel, secs = SECTIONS) {
  if (!pkg) return [];
  return secs.map(([, req, label]) => [label, (sel?.[req] || []).length, pkg.requirements?.[req] || 0]).filter(([, n, need]) => need > 0 && n < need);
}

export function PackageCards({ packages, intl, sym, value, onPick }) {
  return (
    <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="catering-pkg-cards">
      {packages.filter((p) => p.is_active !== false).map((p, i) => {
        const active = value === p.id;
        const req = Object.entries(p.requirements || {}).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(", ");
        return (
          <button key={p.id} type="button" onClick={() => onPick(p)} aria-pressed={active}
            className={`bi-tile !min-h-[150px] gap-1 text-left !items-start relative ${active ? "ring-4 ring-[#750817] ring-offset-2 ring-offset-[#fffaf0] scale-[1.02]" : ""}`}
            style={{ "--bi-fill": metalFill(i) }} data-testid={`catering-pkg-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
            {p.is_popular && <span className="inline-flex items-center gap-1 rounded-full bg-[#F4D58D] px-2 py-0.5 text-xs font-bold text-[#4a080e]" data-testid="catering-pkg-popular"><Crown className="h-3 w-3" />Most Popular</span>}
            <span className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{p.name}</span>
            <span className="text-2xl font-bold">{sym}{pkgPrice(p, intl).toLocaleString("en-IN")}<span className="text-sm font-normal"> / person</span></span>
            <span className="text-xs opacity-90 leading-snug">{req}</span>
            {p.description && <span className="text-xs opacity-90 italic leading-snug line-clamp-3">{p.description}</span>}
            {active && <span className="mt-1 inline-flex items-center gap-1 text-xs font-bold"><Check className="h-4 w-4" />Selected</span>}
          </button>);
      })}
    </div>
  );
}

export function MenuSelector({ pkg, menu, sel, onChange, sections: SECS = SECTIONS }) {
  const [q, setQ] = useState("");
  const sections = useMemo(() => SECS.filter(([, req]) => (pkg?.requirements?.[req] || 0) > 0), [pkg, SECS]);
  if (!pkg) return <p className="text-base text-[#5a3a22]" data-testid="catering-menu-hint">Select a package above to choose the menu.</p>;
  const toggle = (req, need, name) => {
    const cur = sel[req] || [];
    if (cur.includes(name)) return onChange({ ...sel, [req]: cur.filter((x) => x !== name) });
    if (cur.length >= need) { toast.error(`Maximum ${need} allowed for ${pkg.name}. Remove one first, or add extra dishes as a paid add-on below.`); return; }
    onChange({ ...sel, [req]: [...cur, name] });
  };
  const s = q.trim().toLowerCase();
  return (
    <div className="space-y-3" data-testid="catering-menu-selector">
      <div className="flex flex-wrap gap-2" data-testid="catering-counters">
        {sections.map(([, req, label]) => {
          const n = (sel[req] || []).length; const need = pkg.requirements[req];
          return <span key={req} className={`rounded-full border-2 px-3 py-1 text-sm font-bold ${n === need ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-900"}`} data-testid={`catering-counter-${req}`}>{label.split(" (")[0]} {n}/{need}</span>;
        })}
      </div>
      <div className="relative"><Search className="absolute left-3 top-3 h-5 w-5 text-[#8a4f10]" />
        <Input className="pl-10 h-11 text-base" placeholder="Search dishes…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="catering-menu-search" /></div>
      {sections.map(([cat, req, label]) => {
        const need = pkg.requirements[req]; const picked = sel[req] || [];
        const items = (menu[cat] || []).filter((it) => it.is_available !== false && (!s || it.name.toLowerCase().includes(s)));
        if (s && !items.length) return null;
        return (
          <section key={cat} className="rounded-2xl border-2 border-[#ead6a6] bg-[#fffdf8] p-3" data-testid={`catering-section-${cat}`}>
            <h4 className="mb-2 flex items-center gap-2 text-lg font-bold text-[#750817]"><UtensilsCrossed className="h-5 w-5" />{label}
              <span className="text-sm font-semibold text-[#5a3a22]">— choose {need} ({picked.length}/{need})</span></h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {items.map((it) => {
                const on = picked.includes(it.name);
                return (
                  <button key={it.id || it.name} type="button" onClick={() => toggle(req, need, it.name)} aria-pressed={on}
                    className={`rounded-xl border-2 px-3 py-3 text-left text-base font-semibold transition-colors ${on ? "border-[#750817] bg-[#750817] text-[#fff3c4]" : "border-[#ead6a6] bg-white text-[#3b1a12] hover:border-[#d9ae52]"}`}
                    data-testid={`catering-dish-${cat}-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {on && <Check className="mr-1 inline h-4 w-4" />}{it.name}
                  </button>);
              })}
            </div>
          </section>);
      })}
    </div>
  );
}

export function ServicesPicker({ form, setForm, rates, terms, sym }) {
  const cs = form.crockery_service; const ss = form.staff_service;
  const num = (v) => Math.max(0, Number(v) || 0);
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="catering-services">
      <div className="rounded-xl border-2 border-[#ead6a6] bg-white p-3">
        <label className="flex items-start gap-2 text-base font-bold"><Checkbox checked={!!cs} onCheckedChange={(v) => setForm((f) => ({ ...f, crockery_service: v ? { hours: 1, rate: rates.crockery } : null }))} data-testid="catering-crockery-toggle" />
          <span>Crockery &amp; Cutlery Rental<span className="block text-sm font-normal text-[#5a3a22]">Plates, bowls, spoons and serving dishes for guests · {sym}{rates.crockery.toLocaleString("en-IN")} per hour</span>
            <span className="block text-xs font-normal text-[#8a4f10]">{terms}</span></span></label>
        {cs && <div className="mt-2 w-40"><Label>Number of hours</Label><Input type="number" min={1} value={cs.hours} onChange={(e) => setForm((f) => ({ ...f, crockery_service: { ...cs, hours: num(e.target.value) } }))} data-testid="catering-crockery-hours" /></div>}
      </div>
      <div className="rounded-xl border-2 border-[#ead6a6] bg-white p-3">
        <label className="flex items-start gap-2 text-base font-bold"><Checkbox checked={!!ss} onCheckedChange={(v) => setForm((f) => ({ ...f, staff_service: v ? { count: 1, hours: 1, rate: rates.staff } : null }))} data-testid="catering-staff-toggle" />
          <span>Service Staff<span className="block text-sm font-normal text-[#5a3a22]">{sym}{rates.staff.toLocaleString("en-IN")} per person per hour</span></span></label>
        {ss && <div className="mt-2 grid grid-cols-2 gap-2">
          <div><Label>Staff</Label><Input type="number" min={1} value={ss.count} onChange={(e) => setForm((f) => ({ ...f, staff_service: { ...ss, count: num(e.target.value) } }))} data-testid="catering-staff-count" /></div>
          <div><Label>Hours</Label><Input type="number" min={1} value={ss.hours} onChange={(e) => setForm((f) => ({ ...f, staff_service: { ...ss, hours: num(e.target.value) } }))} data-testid="catering-staff-hours" /></div>
        </div>}
      </div>
    </div>
  );
}
